import React from 'react';

export interface ProtractorSvgProps {
  width?: number;
}

// Design space: 200 x 112 (semicircle + base strip)
const VW = 200;
const CX = 100;
const CY = 96;
const R_OUT = 91;   // outer arc radius
const R_IN  = 72;   // inner arc radius (reading band)
const R_LBL = 79;   // label radius

const C_BORDER = '#705DC0';
const C_FILL   = '#f5f2ff';
const C_TICK   = '#4a3a9e';
const C_LABEL  = '#2d1f7a';
const BASE_H   = 14;
const VH = CY + BASE_H + 2;
/** Rendered height ÷ width — use this to reserve layout space without duplicating viewBox values. */
export const PROTRACTOR_ASPECT_RATIO = VH / VW;

const toRad = (deg: number) => (deg * Math.PI) / 180;
const ptOnArc = (r: number, deg: number) => ({
  x: CX + r * Math.cos(toRad(180 - deg)),
  y: CY - r * Math.sin(toRad(180 - deg)),
});

/**
 * Compact semicircle protractor — viewBox-based, scales via width prop.
 */
export const ProtractorSvg: React.FC<ProtractorSvgProps> = ({ width = 180 }) => {
  const ticks: React.ReactNode[] = [];

  for (let deg = 0; deg <= 180; deg++) {
    const isMajor = deg % 10 === 0;
    const isMid   = deg % 5 === 0 && !isMajor;
    const tickLen = isMajor ? 10 : isMid ? 6 : 3.5;
    const sw      = isMajor ? 1.2 : 0.6;

    const outer = ptOnArc(R_OUT, deg);
    const inner = ptOnArc(R_OUT - tickLen, deg);

    ticks.push(
      <line
        key={`t${deg}`}
        x1={outer.x} y1={outer.y}
        x2={inner.x} y2={inner.y}
        stroke={C_TICK}
        strokeWidth={sw}
      />
    );

    if (isMajor && deg > 0 && deg < 180) {
      const lp = ptOnArc(R_LBL, deg);
      ticks.push(
        <text
          key={`l${deg}`}
          x={lp.x} y={lp.y}
          textAnchor="middle"
          dominantBaseline="middle"
          fontSize={deg % 30 === 0 ? 7.5 : 6}
          fontWeight={deg % 30 === 0 ? '700' : '400'}
          fontFamily="Arial, sans-serif"
          fill={C_LABEL}
        >
          {deg}
        </text>
      );
    }
  }

  const oLeft  = ptOnArc(R_OUT, 0);
  const oRight = ptOnArc(R_OUT, 180);
  const iLeft  = ptOnArc(R_IN, 0);
  const iRight = ptOnArc(R_IN, 180);

  return (
    <svg
      width={width}
      viewBox={`0 0 ${VW} ${VH}`}
      xmlns="http://www.w3.org/2000/svg"
      style={{ display: 'block' }}
      role="img"
      aria-label="Protractor"
    >
      {/* Filled semicircle body */}
      <path
        d={`M ${oLeft.x} ${oLeft.y} A ${R_OUT} ${R_OUT} 0 0 1 ${oRight.x} ${oRight.y} Z`}
        fill={C_FILL}
        stroke={C_BORDER}
        strokeWidth={1.5}
      />
      {/* Base strip */}
      <rect x={oLeft.x} y={CY} width={R_OUT * 2} height={BASE_H} fill={C_FILL} stroke={C_BORDER} strokeWidth={1.5} />
      {/* Inner reading arc */}
      <path
        d={`M ${iLeft.x} ${iLeft.y} A ${R_IN} ${R_IN} 0 0 1 ${iRight.x} ${iRight.y}`}
        fill="none"
        stroke={C_BORDER}
        strokeWidth={0.8}
        opacity={0.5}
      />
      {/* Ticks */}
      {ticks}
      {/* Centre dot */}
      <circle cx={CX} cy={CY} r={2.5} fill={C_BORDER} />
      {/* 0° / 180° edge labels */}
      <text x={oLeft.x + 3} y={CY - 3} fontSize={6.5} fontFamily="Arial, sans-serif" fill={C_LABEL}>0</text>
      <text x={oRight.x - 3} y={CY - 3} textAnchor="end" fontSize={6.5} fontFamily="Arial, sans-serif" fill={C_LABEL}>180</text>
    </svg>
  );
};
